import { inject, Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { filter, map, switchMap } from 'rxjs/operators';
import { from, of } from 'rxjs';

import {
  Attachment,
  Pagination,
  PropertySearcherDocumentTypes
} from '@ui/shared/model';
import {
  BlockInput,
  BulkSendMessageInput,
  ConversationInput,
  ConversationMessageInput,
  ConversationMessageResponse,
  ConversationResponse,
  CountUnreadResponse,
  LoadParsedTemplate,
  LoadParsedTemplatesResponse,
  MessageTemplate,
  SendMessageInput
} from '../model/interface';
import { ConversationSenderTypes } from '../model/enum';
import { stripTypenameProperty } from '../../../../utils/strip-typename-property';
import { FileUploadService } from '../../../../infrastructure/file/file-upload.service';
import {
  archiveConversationMutation,
  blockConversation,
  bulkSendMessageMutation,
  conversationMessagesAndDetailsLLQuery,
  conversationMessagesAndDetailsPSQuery,
  countUnread,
  createTemplateMutation,
  deleteTemplateMutation,
  loadParsedTemplatesQuery,
  loadParticipatedAgentsQuery,
  loadTemplateQuery,
  markConversationAsUnreadMutation,
  searchConversationMessagesByConversationIdQuery,
  searchConversationQuery,
  sendMessageInNewConversationMutation,
  sendMessageMutation,
  unarchiveConversationMutation,
  updateTemplateMutation
} from '../../../../infrastructure/base-state/messenger/messenger.queries';

@Injectable()
export class MessengerFacade {
  private apollo = inject(Apollo);
  private fileUploadService = inject(FileUploadService);

  private findUnreadMessageQuery: QueryRef<ConversationMessageResponse>;
  private countUnreadQuery: QueryRef<CountUnreadResponse>;
  private parsedTemplateQuery: QueryRef<LoadParsedTemplatesResponse>;

  public stopCountUnreadPolling() {
    if (!this.countUnreadQuery) return;
    this.countUnreadQuery.stopPolling();
    this.countUnreadQuery = null;
  }

  // TODO remove if not needed: query with polling time auto-starts polling, doesn't it?
  public startFindUnreadMessagesPolling(pollInterval: number) {
    if (!this.findUnreadMessageQuery || !pollInterval) return;
    this.findUnreadMessageQuery.startPolling(pollInterval);
  }

  public stopFindUnreadMessagesPolling() {
    if (!this.findUnreadMessageQuery) return;
    this.findUnreadMessageQuery.stopPolling();
  }

  public searchConversation(data: ConversationInput) {
    return this.apollo
      .query({
        query: searchConversationQuery,
        variables: {
          input: {
            searchTerm: data?.search || undefined,
            agentIds: data?.agents || undefined,
            size: 10,
            page: data?.page || 0,
            conversationId: data?.conversationId,
            propertyId: data?.propertyId,
            dkLevelCustomerSettings: data?.dkLevelCustomerSettings,
            archivedByCustomerOnly: data?.archivedByCustomerOnly
          }
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.searchConversation));
  }

  public blockConversation({ conversationId, blocked }: BlockInput) {
    return this.apollo.mutate<ConversationResponse>({
      mutation: blockConversation,
      variables: {
        input: {
          conversationId,
          blocked
        }
      }
    });
  }

  public searchConversationMessagesByConversationId(
    input: ConversationMessageInput
  ) {
    return this.apollo
      .query({
        query: searchConversationMessagesByConversationIdQuery,
        variables: {
          input
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.searchConversationMessagesByConversationId));
  }

  public conversationMessagesAndDetails(
    input: ConversationMessageInput,
    sender: ConversationSenderTypes
  ) {
    return this.apollo
      .query({
        query:
          sender === ConversationSenderTypes.LANDLORD
            ? conversationMessagesAndDetailsLLQuery
            : conversationMessagesAndDetailsPSQuery,
        variables: {
          input
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res?.data));
  }

  public sendMessageInNewConversation(data: SendMessageInput) {
    const { attachments, ...messageData } = data;
    return this.uploadAttachment(attachments?.[0]).pipe(
      switchMap(attachmentsUploaded => {
        return this.apollo
          .mutate({
            mutation: sendMessageInNewConversationMutation,
            variables: {
              input: {
                ...messageData,
                attachments: attachmentsUploaded
              }
            }
          })
          .pipe(map(res => res.data.sendMessageInNewConversation));
      })
    );
  }

  public sendMessage(data: SendMessageInput) {
    const { attachments, ...messageData } = data;
    return this.uploadAttachment(attachments?.[0]).pipe(
      switchMap(attachmentsUploaded => {
        return this.apollo
          .mutate({
            mutation: sendMessageMutation,
            variables: {
              input: {
                ...messageData,
                attachments: attachmentsUploaded
              }
            }
          })
          .pipe(map(res => res.data.sendMessage));
      })
    );
  }

  public bulkSendMessage(data: BulkSendMessageInput) {
    const { attachments, ...messageData } = data;
    return this.uploadAttachment(attachments?.[0]).pipe(
      switchMap(attachmentsUploaded => {
        return this.apollo.mutate({
          mutation: bulkSendMessageMutation,
          variables: {
            input: {
              ...messageData,
              attachments: attachmentsUploaded
            }
          }
        });
      })
    );
  }

  public loadParticipatedAgents(conversationId: string) {
    return this.apollo
      .query({
        query: loadParticipatedAgentsQuery,
        variables: {
          conversationId
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.loadParticipatedAgents));
  }

  /*
   * we cannot cache this call. Firstly this call needs to be
   * fresh. Secondly caching can cause duplicate messages.
   */
  public findUnreadMessages(
    input: ConversationMessageInput,
    pollInterval: number
  ) {
    this.findUnreadMessageQuery =
      this.apollo.watchQuery<ConversationMessageResponse>({
        query: searchConversationMessagesByConversationIdQuery,
        variables: {
          input: {
            ...input,
            onlyUnread: true
          }
        },
        fetchPolicy: 'no-cache',
        pollInterval
      });

    return this.findUnreadMessageQuery.valueChanges.pipe(
      filter(res => res.dataState === 'complete'),
      map(res => res?.data.searchConversationMessagesByConversationId)
    );
  }

  public countUnread(pollInterval: number) {
    this.stopCountUnreadPolling();

    this.countUnreadQuery = this.apollo.watchQuery<CountUnreadResponse>({
      query: countUnread,
      fetchPolicy: 'no-cache',
      pollInterval
    });

    return this.countUnreadQuery.valueChanges.pipe(
      filter(res => res.dataState === 'complete'),
      map(res => res?.data?.countUnread)
    );
  }

  public refetchCountUnread() {
    return from(this.countUnreadQuery.refetch()).pipe(
      map(res => res?.data?.countUnread)
    );
  }

  public loadTemplate(page: Pagination) {
    return this.apollo
      .query({
        query: loadTemplateQuery,
        variables: {
          page
        },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data.loadTemplate));
  }

  public loadParsedTemplates(input: LoadParsedTemplate) {
    this.parsedTemplateQuery =
      this.apollo.watchQuery<LoadParsedTemplatesResponse>({
        query: loadParsedTemplatesQuery,
        variables: { input },
        fetchPolicy: 'no-cache'
      });

    return this.parsedTemplateQuery.valueChanges.pipe(
      filter(res => res.dataState === 'complete'),
      map(res => res.data.loadParsedTemplates)
    );
  }

  public createTemplate(templateData: MessageTemplate) {
    const { attachments, ...payload } = templateData;
    return this.uploadAttachment(attachments).pipe(
      switchMap(attachmentsUploaded => {
        return this.apollo
          .mutate({
            mutation: createTemplateMutation,
            variables: {
              template: {
                ...payload,
                attachments: attachmentsUploaded
              }
            },
            update: () => this.refetchParsedTemplates()
          })
          .pipe(map(res => res.data.createTemplate));
      })
    );
  }

  public updateTemplate(templateData: MessageTemplate) {
    const { attachments, ...payload } = templateData;
    return this.uploadAttachment(attachments).pipe(
      switchMap(attachmentsUploaded => {
        return this.apollo
          .mutate({
            mutation: updateTemplateMutation,
            variables: {
              template: {
                ...payload,
                attachments: attachmentsUploaded
              }
            },
            update: () => this.refetchParsedTemplates()
          })
          .pipe(map(res => res.data.updateTemplate));
      })
    );
  }

  public deleteTemplate(templateId: string) {
    return this.apollo.mutate({
      mutation: deleteTemplateMutation,
      variables: {
        templateId
      },
      update: () => this.refetchParsedTemplates()
    });
  }

  public markConversationAsUnread(id: string) {
    return this.apollo.mutate({
      mutation: markConversationAsUnreadMutation,
      variables: { id }
    });
  }

  private uploadAttachment(file: Blob | Attachment) {
    if ((file as Attachment)?.url) return of(stripTypenameProperty(file));
    return file
      ? this.fileUploadService.uploadDocuments(
          [{ file } as Attachment],
          PropertySearcherDocumentTypes.SHARED_DOCUMENT
        )
      : of(undefined);
  }

  private refetchParsedTemplates() {
    if (!this.parsedTemplateQuery) {
      return;
    }
    void this.parsedTemplateQuery.refetch();
  }

  public archiveConversation(conversationId: string) {
    return this.apollo.mutate({
      mutation: archiveConversationMutation,
      variables: { conversationId }
    });
  }

  public unarchiveConversation(conversationId: string) {
    return this.apollo.mutate({
      mutation: unarchiveConversationMutation,
      variables: { conversationId }
    });
  }
}
